<template>
  <div ref="scrollBody" class="yk-message-search" @scroll="handleScroll">
    <div v-if="loading && !messageList.length" class="yk-message-search__tip">搜索中...</div>
    <div v-else-if="!messageList.length" class="yk-message-search__tip">暂无搜索结果</div>
    <div v-else class="yk-message-search__list">
      <div
        v-for="messageItem in messageListRepeat"
        :key="messageItem.id"
        class="yk-message-search__item"
        @click="handleSelect(messageItem)"
      >
        <el-image class="yk-message-search__avatar" :src="messageItem.avatar">
          <template #error>
            <el-image :src="defaultAvatar"></el-image>
          </template>
        </el-image>
        <div class="yk-message-search__content">
          <div class="yk-message-search__meta">
            <span class="yk-message-search__sender-name">{{ messageItem.senderName }}</span>
            <span class="yk-message-search__time"
              >{{ messageItem.date }} {{ messageItem.time }}</span
            >
          </div>
          <div class="yk-message-search__text">{{ getMessageText(messageItem) }}</div>
        </div>
      </div>
      <div v-if="loadingMore" class="yk-message-search__tip">加载中...</div>
      <div v-else-if="!hasMore" class="yk-message-search__tip">没有更多消息了</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'YkMessageSearch'
}
</script>

<script setup>
import { onBeforeUnmount, ref, watch, computed } from 'vue'
import { getGroupWxChatRecord, getSingleWxChatRecord } from '@/api/yunke'
import { ENUM_MESSAGE_TYPE } from '../yk-message-core/enum.yk-message-core'

const props = defineProps({
  source: {
    type: String,
    default: 'person'
  },
  params: {
    type: Object,
    default: () => ({
      wechatUserId: '',
      wechatId: '',
      loginUserId: ''
    })
  },
  filter: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['select'])
const scrollBody = ref(null)
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(true)
const messageList = ref([])
const searchTimer = ref(null)
const searchRequestId = ref(0)
const defaultAvatar = require('@/assets/user-default.png')

// 兼容后端做去重
const messageListRepeat = computed(() => {
  const messageIdMap = {}

  return messageList.value.filter((messageItem) => {
    const messageId = messageItem && messageItem.id
    if (!messageId) return true
    if (messageIdMap[messageId]) return false

    messageIdMap[messageId] = true
    return true
  })
})

const formatDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const formatTime = (date) => {
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')
  return `${hour}:${minute}`
}

const parseMessageContent = (content) => {
  if (!content) return {}
  if (typeof content !== 'string') return content

  try {
    return JSON.parse(content)
  } catch (error) {
    return { content }
  }
}

const normalizeMessage = (messageItem) => {
  const isSelf = Number(messageItem.type) === 1
  const rawMessageTime = Number(messageItem.msgTime)
  const messageTime = rawMessageTime < 100000000000 ? rawMessageTime * 1000 : rawMessageTime
  const messageDate = rawMessageTime ? new Date(messageTime) : new Date()

  return {
    id: String(messageItem.id),
    type: messageItem.msgType,
    msgContent: parseMessageContent(messageItem.msgContent),
    date: formatDate(messageDate),
    time: formatTime(messageDate),
    senderId: isSelf ? messageItem.userId : messageItem.wechatId,
    senderName: isSelf ? messageItem.userName : messageItem.friendName,
    avatar: isSelf ? messageItem.userAvatar : messageItem.friendAvatar,
    isSelf
  }
}

const requestMessageList = ({ direction, cursor, filter = {} }) => {
  const { wechatUserId, wechatId, loginUserId, roomId } = props.params || {}
  const requestData = {
    ...filter,
    paginationId: cursor ? Number(cursor) : undefined,
    swipeDirection: direction === 'up' ? 1 : 0
  }

  if (props.source === 'group') {
    requestData.roomId = roomId || ''
    return getGroupWxChatRecord(requestData)
  }

  requestData.wechatUserId = wechatUserId || ''
  requestData.wechatId = wechatId || ''
  requestData.loginUserId = loginUserId || ''

  return getSingleWxChatRecord(requestData)
}

const loadMessageList = (isLoadMore = false, requestId = searchRequestId.value) => {
  if (isLoadMore && (!hasMore.value || loadingMore.value || loading.value)) return

  const cursorMessage = isLoadMore ? messageList.value[messageList.value.length - 1] : null
  const loadingRef = isLoadMore ? loadingMore : loading

  loadingRef.value = true
  return requestMessageList({
    direction: 'down',
    cursor: cursorMessage ? cursorMessage.id : '',
    filter: props.filter
  })
    .then(({ result = {} }) => {
      if (requestId !== searchRequestId.value) return

      const nextMessageList = (result.list || []).map((messageItem) =>
        normalizeMessage(messageItem)
      )
      messageList.value = isLoadMore ? [...messageList.value, ...nextMessageList] : nextMessageList
      hasMore.value = Boolean(result.hasMore)
    })
    .finally(() => {
      if (requestId === searchRequestId.value) {
        loadingRef.value = false
      }
    })
}

const searchMessageList = () => {
  window.clearTimeout(searchTimer.value)
  searchRequestId.value += 1
  messageList.value = []
  hasMore.value = true
  loading.value = false
  loadingMore.value = false

  if (!props.filter.keyword) {
    return
  }

  const requestId = searchRequestId.value
  searchTimer.value = window.setTimeout(() => {
    loadMessageList(false, requestId)
  }, 300)
}

const handleScroll = () => {
  const scrollElement = scrollBody.value
  if (!scrollElement) return

  if (scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight <= 24) {
    loadMessageList(true)
  }
}

const handleSelect = (messageItem) => {
  emit('select', messageItem)
}

const getMessageTextWithType = (messageType, messageContent) => {
  const text = messageContent.title || messageContent.content || ''
  const messageTypeText = `[${messageType.memo}]`
  return text ? `${messageTypeText} ${text}` : messageTypeText
}

const getMessageText = (messageItem) => {
  const messageContent = messageItem.msgContent || {}

  switch (messageItem.type) {
    case ENUM_MESSAGE_TYPE.TEXT.type:
    case ENUM_MESSAGE_TYPE.SYSTEM_MESSAGE.type:
      return messageContent.content || ''
    case ENUM_MESSAGE_TYPE.IMAGE.type:
      return '[图片]'
    case ENUM_MESSAGE_TYPE.VOICE.type:
      return `[语音] ${messageContent.time || 0}''`
    case ENUM_MESSAGE_TYPE.AUDIO_VISUAL.type:
      return `[音视频] ${messageContent.time || 0}''`
    case ENUM_MESSAGE_TYPE.VIDEO.type:
      return '[视频]'
    case ENUM_MESSAGE_TYPE.GIF.type:
      return '[GIF]'
    case ENUM_MESSAGE_TYPE.FILE.type:
      return getMessageTextWithType(ENUM_MESSAGE_TYPE.FILE, messageContent)
    case ENUM_MESSAGE_TYPE.LINK.type:
      return messageContent.title || messageContent.content || ''
    case ENUM_MESSAGE_TYPE.MINI_PROGRAM.type:
      return getMessageTextWithType(ENUM_MESSAGE_TYPE.MINI_PROGRAM, messageContent)
    case ENUM_MESSAGE_TYPE.PERSONAL_CARD.type:
      return getMessageTextWithType(ENUM_MESSAGE_TYPE.PERSONAL_CARD, messageContent)
    case ENUM_MESSAGE_TYPE.LOCATION.type:
      return getMessageTextWithType(ENUM_MESSAGE_TYPE.LOCATION, messageContent)
    case ENUM_MESSAGE_TYPE.QUOTE.type:
      try {
        const parsedContent = JSON.parse(messageContent.content)
        return parsedContent.content || parsedContent.text || parsedContent.title || '[引用]'
      } catch (error) {
        return messageContent.content || '[引用]'
      }
    case ENUM_MESSAGE_TYPE.POKE.type:
      return (messageContent.patMsgs || [])
        .map(({ formUser, pattedUser, content }) => `${formUser}拍了拍${pattedUser}${content}`)
        .join('；')
    default:
      return ''
  }
}

watch(
  () => props.filter,
  () => {
    searchMessageList()
  },
  { immediate: true, deep: true }
)

onBeforeUnmount(() => {
  window.clearTimeout(searchTimer.value)
})
</script>

<style lang="scss" scoped>
.yk-message-search {
  box-sizing: border-box;
  height: 100%;
  padding: 8px 16px;
  overflow-y: auto;
  background: #ffffff;

  &__tip {
    padding: 24px 0;
    font-size: 14px;
    line-height: 20px;
    color: #969799;
    text-align: center;
  }

  &__item {
    display: flex;
    padding: 12px 0;
    cursor: pointer;
    border-bottom: 1px solid #f2f3f5;

    &:hover {
      background: #f7f8fa;
    }
  }

  &__avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    margin-right: 12px;
    overflow: hidden;
    border-radius: 4px;
  }

  &__content {
    min-width: 0;
  }

  &__meta {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
  }

  &__sender-name {
    color: #323233;
  }

  &__time {
    margin-left: 8px;
    color: #969799;
  }

  &__text {
    margin-top: 4px;
    overflow: hidden;
    font-size: 14px;
    line-height: 20px;
    color: #646566;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
